import { useState } from "react";
import type { ProjectionWarning } from "@shared/roi/types";
import { Badge } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { Panel } from "@client/components/Panel";
import { warningKey, warningPanel, warningTarget } from "@client/lib/warningLinks";

/**
 * The engine's structured warnings, as a list you can act on.
 *
 * They are warnings rather than errors on purpose — a half-built scenario still
 * projects — so the list has to be dismissible, or the one about year 4 being
 * unallocated sits there for the twenty minutes it takes to get to year 4. What
 * it must not be is vague: every warning links to the field that caused it,
 * which is why `ProjectionWarning` carries `year` and `packageId` at all.
 */
export function WarningList({
  warnings,
  onGoTo,
}: {
  warnings: ProjectionWarning[];
  /**
   * Opens the dialog that holds the offending field, since the inputs are no
   * longer in the document for a plain anchor to reach. Left optional so the
   * list still renders anywhere the rail does not exist.
   */
  onGoTo?: (warning: ProjectionWarning) => void;
}) {
  const [dismissed, setDismissed] = useState<string[]>([]);

  // A dismissal is remembered by key, so fixing the cause and re-breaking it
  // brings the warning back rather than hiding it for the rest of the session.
  const live = warnings.filter((warning) => !dismissed.includes(warningKey(warning)));

  // Nothing at all when there is nothing to say. A permanent "nothing to flag"
  // card is a card that is empty almost always, sitting between the figures and
  // the charts they explain — and the absence of warnings is already legible
  // from the absence of warnings.
  if (warnings.length === 0) return null;

  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          Warnings <Badge kind="warning">{warnings.length}</Badge>
        </span>
      }
      description="None of these stop a save. Each one names something the projection did that you may not have meant."
      actions={
        dismissed.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setDismissed([])}>
            Show dismissed
          </Button>
        ) : undefined
      }
    >
      {live.length === 0 ? (
        <p className="text-base text-ink-soft">All {warnings.length} dismissed for now.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {live.map((warning) => {
            const key = warningKey(warning);
            const target = warningTarget(warning);
            // A line-item warning has a panel but no anchor of its own; the
            // dialog is still the right place to send someone.
            const reachable = target !== null || warningPanel(warning) !== null;
            return (
              <li
                key={key}
                className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-line px-3 py-2"
                style={{ borderLeft: "3px solid var(--chart-warn)" }}
              >
                <p className="min-w-[14rem] flex-1 text-sm text-ink">
                  <span aria-hidden="true" className="mr-1.5 font-bold text-[var(--chart-warn)]">
                    !
                  </span>
                  {warning.message}
                </p>
                <div className="flex shrink-0 items-center gap-1">
                  {reachable && (
                    <a
                      // Still an anchor, and still pointing at the id the field
                      // renders: the click opens its dialog, but the href keeps
                      // the affordance honest and survives without JavaScript.
                      href={`#${target ?? ""}`}
                      onClick={(event) => {
                        if (!onGoTo) return;
                        event.preventDefault();
                        onGoTo(warning);
                      }}
                      className="rounded-md px-2 py-1 text-xs font-semibold text-azure-deep hover:underline"
                    >
                      Go to it
                    </a>
                  )}
                  <Button
                    variant="quiet"
                    size="sm"
                    aria-label={`Dismiss: ${warning.message}`}
                    onClick={() => setDismissed((current) => [...current, key])}
                  >
                    Dismiss
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
